import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Pagination from "../../components/common/Pagination";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { EmployeeListQuery, fetchEmployees } from "../../store/slices/employeesSlice";

const EmployeesPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { list, totalCount, page, pageSize, loadingList, listError } = useAppSelector((state) => state.employees);

  const [branchId, setBranchId] = useState("");
  const [region, setRegion] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState<EmployeeListQuery["employment_status"] | "">("");
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pageState, setPageState] = useState(1);

  useEffect(() => {
    dispatch(
      fetchEmployees({
        branch_id: branchId ? Number(branchId) : undefined,
        region: region || undefined,
        employment_status: employmentStatus || undefined,
        q: query || undefined,
        page: pageState,
        page_size: 20,
      })
    );
  }, [branchId, dispatch, employmentStatus, pageState, query, region]);

  const onSubmitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery(searchInput.trim());
    setPageState(1);
  };

  return (
    <section className="admin-page">
      <h2 className="section-title">직원 관리</h2>

      <header className="list-toolbar">
        <form className="toolbar-left" onSubmit={onSubmitSearch}>
          <input
            className="filter-input"
            placeholder="지점 ID"
            value={branchId}
            onChange={(event) => {
              setBranchId(event.target.value);
              setPageState(1);
            }}
          />
          <input
            className="filter-input"
            placeholder="지역"
            value={region}
            onChange={(event) => {
              setRegion(event.target.value);
              setPageState(1);
            }}
          />
          <select
            className="chip-select"
            value={employmentStatus}
            onChange={(event) => {
              setEmploymentStatus(event.target.value as EmployeeListQuery["employment_status"] | "");
              setPageState(1);
            }}
          >
            <option value="">전체 상태</option>
            <option value="active">재직</option>
            <option value="retired">퇴사</option>
          </select>
          <div className="search-box">
            <input
              placeholder="직원명/직원번호/지점명 검색"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            <button className="inline-search-btn" type="submit">
              검색
            </button>
          </div>
        </form>
        <p className="summary-text">총 <strong>{totalCount.toLocaleString()}</strong>명</p>
      </header>

    <div className="table-shell" style={{ overflowX: 'auto' }}>
      <table className="admin-table compact" style={{ whiteSpace: 'nowrap' }}>
          <thead>
            <tr>
              <th>직원번호</th>
              <th>이름</th>
              <th>가입여부</th>
              <th>지점</th>
              <th>지역</th>
              <th>연락처</th>
              <th>근무시간</th>
              <th>상태</th>
              <th style={{ textAlign: 'center' }}>상세</th>
              <th style={{ textAlign: 'center' }}>계약서</th>
              <th style={{ textAlign: 'center' }}>급여명세서</th>
            </tr>
          </thead>
          <tbody>
            {list.map((employee) => (
              <tr key={employee.employee_id}>
                <td>{employee.employee_number}</td>
                <td>{employee.employee_name}</td>
                <td>
                  <span className={`branch-status-badge ${employee.is_guest ?? (employee.linked_user_id === null) ? 'branch-status-badge--neutral' : 'branch-status-badge--operating'}`}>
                    {employee.is_guest ?? (employee.linked_user_id === null) ? '비회원' : '회원'}
                  </span>
                </td>
                <td>{employee.branch_name}</td>
                <td>{employee.region_label}</td>
                <td>{employee.phone_number}</td>
                <td>{employee.weekly_work_label}</td>
                <td>{employee.employment_status_label}</td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="detail-icon-btn"
                    type="button"
                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                    onClick={() => navigate(`/admin/employees/${employee.employee_id}`)}
                  >
                    상세
                  </button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="detail-icon-btn"
                    type="button"
                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', backgroundColor: '#f0f5ff', color: '#5696ff', border: '1px solid #5696ff' }}
                    onClick={() => navigate(`/admin/employees/${employee.employee_id}/contracts`)}
                  >
                    계약서
                  </button>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="detail-icon-btn"
                    type="button"
                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', backgroundColor: '#f0f5ff', color: '#5696ff', border: '1px solid #5696ff' }}
                    onClick={() => navigate(`/admin/employees/${employee.employee_id}/payrolls`)}
                  >
                    급여명세서
                  </button>
                </td>
              </tr>
            ))}
            {!loadingList && list.length === 0 && (
              <tr>
                <td className="empty-row" colSpan={10}>
                  조회된 직원 정보가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loadingList && <p className="loading-text">직원 목록을 불러오는 중입니다...</p>}
      {listError && <p className="error-text">{listError}</p>}
      <Pagination page={page || pageState} pageSize={pageSize || 20} totalCount={totalCount} onPageChange={setPageState} />
    </section>
  );
};

export default EmployeesPage;
